import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  LS_KEY,
  RoleContext,
  type RoleContextValue,
  type RoleId,
  readInitialRole,
} from './roles';

/**
 * Client-side role mock (per TC-013, FR-002).
 *
 * No real authentication. Role switches update UI in <100ms without reload (US-002).
 *
 * Sprint 2 note: This file now exports only the `RoleProvider` component so
 * that fast-refresh (`react-refresh/only-export-components`) stays green as
 * the codebase grows. Types, constants, and the `useRole` hook live in
 * `./roles` — import from there for everything except the provider itself.
 */

export function RoleProvider({ children }: { children: ReactNode }) {
  const [role, setRoleState] = useState<RoleId>(readInitialRole);

  const setRole = useCallback((next: RoleId) => {
    setRoleState(next);
    try {
      window.localStorage.setItem(LS_KEY, next);
    } catch {
      // localStorage blocked — fail soft (TC-008: UI prefs only)
    }
  }, []);

  const value = useMemo<RoleContextValue>(() => ({ role, setRole }), [role, setRole]);
  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}
